import { useAuth } from '@/features/auth/hooks';
import { useProviderMemberships } from '@/features/shop-discovery/hooks/staff/queries';

const mapShopRole = (
  shopRole: string | undefined,
  fallbackRole: string | null | undefined,
): string | undefined => {
  const normalizedFallback = fallbackRole ?? undefined;
  if (!shopRole) return normalizedFallback;
  switch (shopRole) {
    case 'OWNER':
      return 'shop_owner';
    case 'MANAGER':
      return 'shop_manager';
    case 'STAFF':
    case 'RENTER':
      return 'staff_provider';
    default:
      return normalizedFallback;
  }
};

interface MembershipShape {
  isActive?: boolean;
  canManageServices?: boolean | null;
  shopId?: string;
  shop?: { id?: string };
}

/**
 * Pick the membership that governs permissions for the requested shop.
 * When `shopId` is provided we only consider that exact shop; otherwise we
 * fall back to the legacy "first active" behavior to preserve back-compat.
 */
const resolveMembership = (
  memberships: MembershipShape[] | undefined,
  shopId?: string,
): MembershipShape | undefined => {
  if (!memberships) return undefined;
  if (shopId) {
    return memberships.find(
      m =>
        m.isActive !== false &&
        (m.shopId === shopId || m.shop?.id === shopId),
    );
  }
  return memberships.find(m => m.isActive !== false);
};

/**
 * Hook to check if the current user has permission to manage services.
 * Accepts an optional shopRole override (from useShopContext) for cross-shop
 * scenarios where the global auth role doesn't reflect the user's role in
 * the current shop, and a shopId so staff permissions are resolved from the
 * membership that matches the active shop rather than an arbitrary row.
 */
export const useServicePermission = (
  shopRole?: string,
  shopId?: string,
): boolean => {
  const { role, providerId } = useAuth();

  // Fetch provider memberships (only runs for staff providers with providerId)
  const { data: memberships } = useProviderMemberships(
    providerId || '',
    true, // Only active memberships
  );

  const effectiveRole = mapShopRole(shopRole, role);

  // Shop owners, managers, and independent providers always have access
  if (
    effectiveRole === 'shop_owner' ||
    effectiveRole === 'shop_manager' ||
    effectiveRole === 'independent_provider'
  ) {
    return true;
  }

  // Staff providers need explicit permission from the membership that
  // corresponds to the active shop. Without a shop context, fall back to
  // the first-active membership for back-compat.
  if (effectiveRole === 'staff_provider') {
    const membership = resolveMembership(
      memberships as MembershipShape[] | undefined,
      shopId,
    );
    return !!membership?.canManageServices;
  }

  return false;
};

/**
 * Hook to check if the current user can view all services or only their own
 * Returns 'all' for owners, 'own' for independent providers and permitted staff
 */
export const useServiceViewScope = (
  shopRole?: string,
  shopId?: string,
): 'all' | 'own' | 'none' => {
  const { role, providerId } = useAuth();

  // Fetch provider memberships (only runs for staff providers with providerId)
  const { data: memberships } = useProviderMemberships(
    providerId || '',
    true, // Only active memberships
  );

  const effectiveRole = mapShopRole(shopRole, role);

  // Shop owners and managers can see all services
  if (effectiveRole === 'shop_owner' || effectiveRole === 'shop_manager') {
    return 'all';
  }

  // Independent providers see their own
  if (effectiveRole === 'independent_provider') {
    return 'own';
  }

  // Staff with permission see their own
  if (effectiveRole === 'staff_provider') {
    const membership = resolveMembership(
      memberships as MembershipShape[] | undefined,
      shopId,
    );
    if (membership?.canManageServices) {
      return 'own';
    }
  }

  return 'none';
};
